import React, { useEffect, useState } from "react";
import { X, PlayCircle, Link2, Check } from "lucide-react";
import HeroDemo, { SLIDES, SLIDE_CAPTIONS, SLIDE_COMPONENTS } from "@/components/HeroDemo";

const TOTAL_SECONDS = 60;
const PER_SLIDE_MS = (TOTAL_SECONDS * 1000) / SLIDE_COMPONENTS.length;

function buildShareUrl() {
  if (typeof window === "undefined") return "";
  const u = new URL(window.location.origin + "/");
  u.searchParams.set("demo", "open");
  return u.toString();
}

export default function HeroDemoModal({ open, onClose }) {
  const [activeSlide, setActiveSlide] = useState(SLIDES[0]);
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const [copied, setCopied] = useState(false);

  const copyShareLink = async () => {
    const url = buildShareUrl();
    try {
      await navigator.clipboard.writeText(url);
    } catch (clipboardErr) {
      console.warn("[HeroDemoModal] navigator.clipboard failed, falling back to execCommand", clipboardErr);
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch (execErr) {
        console.error("[HeroDemoModal] execCommand copy failed", execErr);
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  // Lock body scroll when open + handle Esc.
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // 60-second countdown → auto-close.
  useEffect(() => {
    if (!open) {
      setRemaining(TOTAL_SECONDS);
      return;
    }
    setRemaining(TOTAL_SECONDS);
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          onClose?.();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open, onClose]);

  if (!open) return null;

  const caption = SLIDE_CAPTIONS[activeSlide] || {};
  const elapsed = TOTAL_SECONDS - remaining;
  const progress = (elapsed / TOTAL_SECONDS) * 100;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8 animate-[fadeIn_0.25s_ease-out_both]"
      onClick={onClose}
      data-testid="hero-demo-modal"
      role="dialog"
      aria-modal="true"
      aria-label="GuestIntel 60-second product demo"
    >
      <div
        className="relative w-full max-w-6xl bg-white rounded-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-zinc-50">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <PlayCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Product demo · 60s</div>
              <div className="text-sm font-display font-bold">GuestIntel in action</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyShareLink}
              className={`hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                copied
                  ? "border-teal/40 bg-teal/10 text-teal"
                  : "border-border hover:bg-zinc-100 text-zinc-700"
              }`}
              data-testid="demo-copy-link"
              aria-label="Copy shareable demo link"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
              <span>{copied ? "Link copied" : "Copy share link"}</span>
            </button>
            <span className="text-xs font-mono text-muted-foreground tabular-nums" data-testid="demo-countdown">
              0:{String(remaining).padStart(2, "0")}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-md border border-border hover:bg-zinc-100 flex items-center justify-center"
              data-testid="demo-close"
              aria-label="Close demo"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-zinc-100">
          <div className="h-full bg-teal transition-[width] duration-1000 ease-linear" style={{ width: `${progress}%` }} />
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          <div className="lg:col-span-3 border-r border-border">
            <HeroDemo intervalMs={PER_SLIDE_MS} onSlideChange={(_, name) => setActiveSlide(name)} height="h-[460px] lg:h-[520px]" />
          </div>
          <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col gap-4 bg-gradient-to-br from-white to-zinc-50">
            <div className="label-eyebrow text-teal">{`Step ${SLIDES.indexOf(activeSlide) + 1} of ${SLIDES.length}`}</div>
            <h2 className="font-display font-black text-3xl tracking-tight" data-testid="demo-caption-title">
              {caption.title}
            </h2>
            <p className="text-sm text-zinc-700 leading-relaxed" data-testid="demo-caption-body">
              {caption.body}
            </p>

            <ul className="mt-auto space-y-2 text-xs text-muted-foreground">
              {SLIDES.map((s, i) => (
                <li key={s} className={`flex items-center gap-2 ${s === activeSlide ? "text-zinc-900" : ""}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s === activeSlide ? "bg-teal" : "bg-zinc-300"}`} />
                  <span className="capitalize">{i + 1}. {SLIDE_CAPTIONS[s].title}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-border pt-3 mt-2 text-[11px] text-muted-foreground">
              Press <kbd className="px-1.5 py-0.5 border border-border rounded font-mono bg-white">Esc</kbd> to close. Auto-closes when the timer ends.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
